import { useEffect, useState } from "react";
import { z } from "zod";
import { useDynamicConfig } from "../lib/delivery";
import { DynamicConfigs } from "../lib/delivery_keys";

const BetaWallConfigSchema = z.object({
  enabled: z.boolean().default(false),
  startsAt: z.coerce.date().optional(),
});

export type BetaWallConfig = z.infer<typeof BetaWallConfigSchema>;

const POLL_MS = 5 * 60 * 1000;
/** The longest delay setTimeout honours. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/** Whether the wall applies at `now`: enabled, and its start date (if any) reached. */
export function isBetaWallActive(config: BetaWallConfig | null, now: number): boolean {
  if (!config?.enabled) return false;
  return !config.startsAt || config.startsAt.getTime() <= now;
}

/**
 * Whether the beta sign-in wall is up. Off when the config is missing or unreachable. Polls the
 * Statsig config and flips at the start date without a reload.
 */
export function useBetaWall(): boolean {
  const config = useDynamicConfig(DynamicConfigs.betaWall, BetaWallConfigSchema, POLL_MS);
  const [now, setNow] = useState(() => Date.now());
  const startsAt = config?.startsAt?.getTime();

  useEffect(() => {
    if (!startsAt || startsAt <= Date.now()) return;
    const id = setTimeout(
      () => setNow(Date.now()),
      Math.min(startsAt - Date.now(), MAX_TIMEOUT_MS)
    );
    return () => clearTimeout(id);
  }, [startsAt, now]);

  return isBetaWallActive(config, now);
}
